import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import useExperiment from "../hooks/useExperiment";
import { EXPERIMENTS } from "../lib/experiments";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import TestimonialCarousel from "../components/TestimonialCarousel";
import ReelCarousel from "../components/ReelCarousel";
import TrustBadgeRow from "../components/TrustBadgeRow";
import HeroMedia from "../components/HeroMedia";
import BottomCta from "../components/BottomCta";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import {
  Activity,
  HeartPulse,
  Leaf,
  LineChart,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  CheckCircle2,
} from "lucide-react";

const features = [
  {
    icon: Stethoscope,
    title: "Smart Health Assessment",
    desc: "A guided questionnaire captures your body, lifestyle and health priorities.",
  },
  {
    icon: LineChart,
    title: "BMI & Risk Analysis",
    desc: "Instant BMI, metabolic, cardiovascular, hormonal and skin risk indicators.",
  },
  {
    icon: Sparkles,
    title: "AI-Curated Products",
    desc: "Claude 4.5 pairs you with the right Unicity Feel Great products — no guesswork.",
  },
  {
    icon: MessageCircle,
    title: "Talk to a Real Expert",
    desc: "One tap opens WhatsApp with a wellness coach who knows your report.",
  },
];

const steps = [
  {
    n: "01",
    title: "Create your profile",
    body: "Sign up in 30 seconds with email. No credit card, ever.",
  },
  {
    n: "02",
    title: "Answer a 3-minute assessment",
    body: "Age, body, lifestyle and conditions — from PCOS to diabetes to low energy.",
  },
  {
    n: "03",
    title: "Get a personalized plan",
    body: "Clear health report + Feel Great System recommendations tailored to you.",
  },
];

const trust = [
  "HIPAA-aware data handling",
  "No spam, no selling data",
  "Cancel any time",
  "Evidence-based recommendations",
];

export default function LandingPage() {
  const [whatsapp, setWhatsapp] = useState("");
  const headline = useExperiment(EXPERIMENTS.LANDING_HEADLINE);
  const cta = useExperiment(EXPERIMENTS.LANDING_CTA);

  useSeo({
    title: "Smart Health Assessment & Personalized Wellness Plan | Feel Greats",
    description:
      "Take a 3-minute health assessment to receive a personalized wellness plan, metabolism insights, and tailored wellness support from Feel Greats.",
    canonical: `${PROD_DOMAIN}/`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Smart Health Assessment & Personalized Wellness Plan | Feel Greats",
        "url": `${PROD_DOMAIN}/`,
        "description":
          "Take a 3-minute health assessment to receive a personalized wellness plan, metabolism insights, and tailored wellness support from Feel Greats.",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How does the Feel Greats health assessment work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text":
                "You answer about 3 minutes of questions covering your goals, current wellness challenges, and lifestyle. We then match you to the Unicity Feel Great System products that best support your specific situation.",
            },
          },
          {
            "@type": "Question",
            "name": "Are the products medical or pharmaceutical?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text":
                "No. Feel Greats provides wellness recommendations only — they're nutritional supplements and lifestyle tools, not medical treatments. Always check with a clinician about your individual situation.",
            },
          },
          {
            "@type": "Question",
            "name": "Can I cancel or get a refund?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text":
                "Yes. We offer a transparent refund policy on first-time purchases. See our refund page for full details.",
            },
          },
          {
            "@type": "Question",
            "name": "Do you offer flexible payment options?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text":
                "Yes — checkout supports Apple Pay, Google Pay, Klarna and Clearpay alongside cards. Buy now, pay later is available where supported by your bank.",
            },
          },
        ],
      },
    ],
  });

  useEffect(() => {
    track.landingView();
    api
      .get("/config")
      .then(({ data }) => setWhatsapp(data?.whatsapp_number || ""))
      .catch(() => setWhatsapp(""));
  }, []);
  const waText = "Hi, I completed my health assessment. Can you guide me further?";
  const waUrl = whatsapp
    ? `https://wa.me/${whatsapp}?text=${encodeURIComponent(waText)}`
    : `https://wa.me/?text=${encodeURIComponent(waText)}`;
  return (
    <main className="bg-leaf-pattern" data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-32 pb-16 lg:pb-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-none rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] font-semibold mb-6">
              <Leaf className="w-3.5 h-3.5 mr-1.5" /> {headline.content.eyebrow}
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-emerald-950 leading-[1.05]" data-testid="hero-headline" data-variant={headline.variant}>
              {headline.content.line1} <span className="italic text-emerald-700">{headline.content.line1_em}</span>
              <br />
              {headline.content.line2} <span className="gold-accent font-medium">{headline.content.line2_em}</span>
            </h1>
            <p className="mt-6 text-lg text-emerald-900/70 max-w-xl leading-relaxed">
              {headline.content.sub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-start-cta" data-variant={cta.variant}>
                <Button className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6 text-base">
                  {cta.content.text}
                </Button>
              </Link>
              <Link to="/shop" data-testid="hero-shop-cta">
                <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 px-8 py-6 text-base">
                  Explore the wellness range
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-login-cta">
                <Button variant="ghost" className="rounded-full text-emerald-900 hover:bg-emerald-50 px-6 py-6 text-base">
                  I already have an account
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {trust.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-emerald-900/70">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700" /> {t}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <HeroMedia />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-3">
            What you'll get
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-emerald-950">
            A smarter way to understand and improve your wellness.
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="bg-white border border-emerald-100 rounded-2xl p-6 hover:-translate-y-1 transition-transform"
              data-testid={`feature-card-${i}`}
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 mb-4">
                <f.icon className="w-5 h-5" strokeWidth={1.6} />
              </span>
              <h3 className="font-display text-lg font-medium text-emerald-950 mb-2">{f.title}</h3>
              <p className="text-sm text-emerald-900/70 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-emerald-50/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-3 gap-10">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-3">
                How it works
              </div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-emerald-950 mb-4">
                From unsure to empowered in 3 minutes.
              </h2>
              <p className="text-emerald-900/70 leading-relaxed">
                No intimidating charts. No jargon. Just a simple flow that respects your time and
                gives you a clear next step.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
              {steps.map((s) => (
                <div
                  key={s.n}
                  className="bg-white rounded-2xl border border-emerald-100 p-6"
                  data-testid={`step-${s.n}`}
                >
                  <div className="text-xs tracking-[0.2em] uppercase text-amber-600 font-semibold">
                    Step {s.n}
                  </div>
                  <h3 className="font-display text-lg font-medium text-emerald-950 mt-2 mb-2">
                    {s.title}
                  </h3>
                  <p className="text-sm text-emerald-900/70">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <TrustBadgeRow />
      </section>

      {/* Real journeys (reels) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <ReelCarousel />
      </section>

      {/* Testimonials */}
      <section className="bg-emerald-50/40 border-y border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              In our members' words
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 mt-1 leading-tight">
              The kind of feedback we love getting on a Monday morning.
            </h2>
          </div>
          <TestimonialCarousel />
        </div>
      </section>

      {/* Closing CTA — admin-editable */}
      <BottomCta />
    </main>
  );
}
